import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import { ProductFeature, ProductFeatureType } from '../../graphql.generated';
import Loading from '../Loading';
import Maintenance from '../Maintenance';

import { useFeaturesQuery } from './Features.generated';

export const FeatureContext = createContext<ProductFeature[] | undefined>(
  undefined,
);

export const useFeatures = (): ProductFeatureType[] => {
  const features = useContext(FeatureContext);

  if (!features) {
    throw new Error('No features');
  }

  return useMemo(() => features.map(({ type }) => type), [features]);
};

const FeatureProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const {
    previousData,
    data = previousData,
    error,
    loading,
  } = useFeaturesQuery({
    fetchPolicy: 'network-only',
  });

  const features = useMemo(
    () => data?.organization?.productFeatures,
    [data?.organization?.productFeatures],
  );

  if (error) {
    return <Maintenance />;
  }

  if (loading && !features) {
    return <Loading />;
  }

  return (
    <FeatureContext.Provider value={features}>
      {children}
    </FeatureContext.Provider>
  );
};

FeatureProvider.displayName = 'FeatureProvider';

export default FeatureProvider;
